import { RootState } from "@/store/Store";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface BaseFieldInterface {
    valid: boolean,
    error: string,
}

interface StringFieldInterface extends BaseFieldInterface {
    value: string,
}
interface NumberFieldInterface extends BaseFieldInterface {
    value: number,
}
interface ZeroOrOneInterface extends BaseFieldInterface {
    value: 1 | 0,
}

export interface CreateEditBannerFormInterface {
    form: {
        name: StringFieldInterface,
        image: StringFieldInterface,
        desc: StringFieldInterface,
        is_public: ZeroOrOneInterface,
        date_end: StringFieldInterface,
        delay: NumberFieldInterface,
        url: StringFieldInterface,
    },
    formVariant: NewOrEditBanner,
}

function getInitialState() {
    const initialState: CreateEditBannerFormInterface = {
        form: {
            name: {
                value: "",
                valid: true,
                error: "",
            },
            image: {
                value: "",
                valid: true,
                error: "",
            },
            desc: {
                value: "",
                valid: true,
                error: "",
            },
            is_public: {
                value: 1,
                valid: true,
                error: "",
            },
            date_end: {
                value: "",
                valid: true,
                error: "",
            },
            delay: {
                value: 1,
                valid: true,
                error: "",
            },
            url: {
                value: "",
                valid: true,
                error: "",
            },
        },
        formVariant: "new",
    }
    return initialState;
}

export type NewOrEditBanner = "new" | "edit";

export const CreateEditBannerFormSlice = createSlice({
    name: "CreateEditBannerFormSlice",
    initialState: getInitialState(),
    reducers: {
        setFormValue: (state: CreateEditBannerFormInterface, action: PayloadAction<{name: keyof CreateEditBannerFormInterface["form"], value: string | number}>) => {
            const {name, value} = action.payload;
            state.form[name].value = value;
        },
        setFormValid: (state: CreateEditBannerFormInterface, action: PayloadAction<{name: keyof CreateEditBannerFormInterface["form"], valid: boolean}>) => {
            const {name, valid} = action.payload;
            state.form[name].valid = valid;
        },
        setFormError: (state: CreateEditBannerFormInterface, action: PayloadAction<{name: keyof CreateEditBannerFormInterface["form"], error: string}>) => {
            const {name, error} = action.payload;
            state.form[name].error = error;
        },
        clearForm: (state: CreateEditBannerFormInterface, action: PayloadAction<void>) => {
            const initialState = getInitialState();
            state.form = initialState.form;
        },
        changeFormVariant: (state: CreateEditBannerFormInterface, action: PayloadAction<NewOrEditBanner>) => {
            state.formVariant = action.payload;
        },
    },
});

export const {
    setFormValue,
    setFormValid,
    setFormError,
    clearForm,
    changeFormVariant,
} = CreateEditBannerFormSlice.actions;

export const getSelectorForValue = (name: keyof CreateEditBannerFormInterface["form"]) => (state: RootState) => state.createEditBannerForm.form[name].value;
export const getSelectorForError = (name: keyof CreateEditBannerFormInterface["form"]) => (state: RootState) => state.createEditBannerForm.form[name].error;
export const getSelectorForValid = (name: keyof CreateEditBannerFormInterface["form"]) => (state: RootState) => state.createEditBannerForm.form[name].valid;
export const formSelector = (state: RootState) => state.createEditBannerForm.form;
export const formValidSelector = (state: RootState) => {
    return state.createEditBannerForm.form.name.value.trim() !== "" &&
    state.createEditBannerForm.form.image.value !== "" &&
    state.createEditBannerForm.form.date_end.valid &&
    ! isNaN(state.createEditBannerForm.form.delay.value)
    ;
};

export const getFormVariant = (state: RootState) => state.createEditBannerForm.formVariant;

export const CreateEditBannerFormReducer = CreateEditBannerFormSlice.reducer;
